import { parseDateOnlyToLocal } from '../../../../core/utils/date-only.util';
import { OperacionSocietaria } from '../mock-data/operaciones.mock';
import { ConstitutionSnapshot } from '../models/constitution-snapshot.model';

export type EstadoSectionKey = 'objetoSocial' | 'capitalSocial' | 'socios' | 'administracion' | 'apoderados';

const ESTADO_SECTIONS: EstadoSectionKey[] = [
  'objetoSocial', 'capitalSocial', 'socios', 'administracion', 'apoderados',
];

function isEstadoSection(section: string): section is EstadoSectionKey {
  return (ESTADO_SECTIONS as string[]).includes(section);
}

const EMPTY_ESCRITURA: ConstitutionSnapshot['escritura'] = {
  fecha: '', concepto: '', notario: '', protocolo: '',
  provinciaRM: '', fechaInscripcion: '', tomo: '', folio: '',
  hoja: '', seccion: '', libro: '', numInscripcion: '',
};

/** Calendar day at local midnight. Uses the yyyy-MM-dd prefix so UTC midnight does not shift the day. */
export function calendarTimestamp(value: string | Date | null | undefined): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'string' && value.includes('/')) {
    return parseDdMmYyyy(value);
  }
  const d = parseDateOnlyToLocal(value);
  if (!d) return 0;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function formatDeedDate(value: string | Date | null | undefined): string {
  const ts = calendarTimestamp(value);
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Deed day, or the creation day when the deed date is missing. */
export function resolveDeedTime(
  deedDate: string | Date | null | undefined,
  createdAt?: string | null,
): number {
  const fromDeed = calendarTimestamp(deedDate);
  if (fromDeed) return fromDeed;
  if (!createdAt) return 0;
  return calendarTimestamp(createdAt);
}

export function endOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();
}

export function operationDeedTime(op: Pick<OperacionSocietaria, 'deedTime' | 'fechaEscritura'>): number {
  if (op.deedTime) return op.deedTime;
  return parseDdMmYyyy(op.fechaEscritura);
}

export function isDeedOnOrBefore(
  op: Pick<OperacionSocietaria, 'deedTime' | 'fechaEscritura'>,
  snapshotDate: Date,
): boolean {
  const t = operationDeedTime(op);
  return t > 0 && t <= endOfLocalDay(snapshotDate);
}

/** Parse dd/MM/yyyy. Two-digit years are 20xx, not year 25. */
export function parseDdMmYyyy(s: string | undefined): number {
  if (!s) return 0;
  const parts = s.split('/').map(part => Number(String(part).trim()));
  const [day, month, yearRaw] = parts;
  if (!day || !month || !yearRaw) return 0;
  const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
  return new Date(year, month - 1, day).getTime();
}

export function isEstadoSectionPopulated(section: EstadoSectionKey, snapshot: ConstitutionSnapshot): boolean {
  switch (section) {
    case 'objetoSocial':
      return !!(snapshot.objetoSocial?.codigoCnae || snapshot.objetoSocial?.descripcion);
    case 'capitalSocial': {
      const cap = snapshot.capitalSocial;
      if (!cap) return false;
      if (cap.hasSeries) return cap.series.some(s => (s.totalAcciones ?? 0) > 0 || (s.valorNominal ?? 0) > 0);
      return cap.totalAcciones > 0 || (cap.valorNominal ?? 0) > 0;
    }
    case 'socios':
      return snapshot.socios?.some(s => !!(s.nombre?.trim() || s.nif?.trim())) ?? false;
    case 'administracion':
      return snapshot.administracion?.miembros?.some(m => !!(m.nombre?.trim() || m.nif?.trim())) ?? false;
    case 'apoderados':
      return snapshot.apoderados?.some(a => !!(a.nombre?.trim() || a.nif?.trim())) ?? false;
    default:
      return false;
  }
}

/**
 * Alta/baja operations may replace a section with an empty list on purpose.
 * Every other operation keeps the previous value when its payload for that section is empty.
 * Statute amendments never touch the administration organ or attorneys.
 */
function appliesSectionEvenIfEmpty(op: OperacionSocietaria, section: EstadoSectionKey): boolean {
  if (op.categoria === 'modificacion_estatutos') return false;
  if (section === 'administracion') return op.subtipo === 'alta_baja_cambio_admin';
  if (section === 'apoderados') return op.subtipo === 'alta_baja_apoderados';
  return false;
}

function sectionAllowedForOperation(op: OperacionSocietaria, section: EstadoSectionKey): boolean {
  if (op.categoria === 'modificacion_estatutos' && (section === 'administracion' || section === 'apoderados')) {
    return false;
  }
  return true;
}

function emptySnapshot(): ConstitutionSnapshot {
  return {
    escritura: { ...EMPTY_ESCRITURA },
    objetoSocial: { codigoCnae: '', descripcion: '' },
    capitalSocial: { hasSeries: false, totalCapital: '', totalAcciones: 0, series: [] },
    socios: [],
    administracion: { sistema: '', miembros: [] },
    apoderados: [],
  };
}

function snapshotHasContent(snapshot: ConstitutionSnapshot): boolean {
  if (snapshot.escritura.fecha || snapshot.escritura.notario || snapshot.escritura.protocolo) return true;
  return ESTADO_SECTIONS.some(section => isEstadoSectionPopulated(section, snapshot));
}

/** Latest operation (by deed date) that sets the given section in the estado actual. */
export function findLatestOperationForSection(
  ops: OperacionSocietaria[],
  section: EstadoSectionKey,
): OperacionSocietaria | null {
  const sorted = [...ops].sort((a, b) => operationDeedTime(b) - operationDeedTime(a));
  return sorted.find(op =>
    !!op.snapshot
    && (op.seccionesAfectadas ?? []).includes(section)
    && sectionAllowedForOperation(op, section)
    && (isEstadoSectionPopulated(section, op.snapshot) || appliesSectionEvenIfEmpty(op, section)),
  ) ?? null;
}

/** Chronological fold of the sections each operation actually changes. */
export function buildHistoricalEstadoActual(ops: OperacionSocietaria[]): ConstitutionSnapshot | null {
  if (!ops.length) return null;

  const result = emptySnapshot();
  const sorted = [...ops].sort((a, b) => operationDeedTime(a) - operationDeedTime(b));

  for (const op of sorted) {
    if (!op.snapshot) continue;

    for (const section of op.seccionesAfectadas ?? []) {
      if (!isEstadoSection(section)) continue;
      if (!sectionAllowedForOperation(op, section)) continue;
      if (!isEstadoSectionPopulated(section, op.snapshot) && !appliesSectionEvenIfEmpty(op, section)) {
        continue;
      }
      (result as unknown as Record<string, unknown>)[section] = op.snapshot[section];
    }

    if (op.snapshot.escritura?.fecha || op.snapshot.escritura?.notario) {
      result.escritura = op.snapshot.escritura;
    }
  }

  return snapshotHasContent(result) ? result : null;
}

export function mergeEstadoActualWithOperations(
  apiSnapshot: ConstitutionSnapshot,
  ops: OperacionSocietaria[],
): ConstitutionSnapshot {
  const opsSnapshot = buildHistoricalEstadoActual(ops);
  if (!opsSnapshot) return apiSnapshot;

  const merged: ConstitutionSnapshot = { ...apiSnapshot };
  for (const section of ESTADO_SECTIONS) {
    if (!isEstadoSectionPopulated(section, opsSnapshot)) continue;
    switch (section) {
      case 'objetoSocial':
        merged.objetoSocial = opsSnapshot.objetoSocial;
        break;
      case 'capitalSocial':
        merged.capitalSocial = opsSnapshot.capitalSocial;
        break;
      case 'socios':
        merged.socios = opsSnapshot.socios;
        break;
      case 'administracion':
        merged.administracion = opsSnapshot.administracion;
        break;
      case 'apoderados':
        merged.apoderados = opsSnapshot.apoderados;
        break;
    }
  }
  if (opsSnapshot.escritura.fecha || opsSnapshot.escritura.notario) {
    merged.escritura = opsSnapshot.escritura;
  }
  return merged;
}

/** El estado actual no lista a los miembros del órgano de administración con baja a esa fecha (la baja sigue en el detalle de la operación). */
export function sinMiembrosDadosDeBaja<T extends ConstitutionSnapshot | null>(snapshot: T, hasta: number = Date.now()): T {
  if (!snapshot?.administracion) return snapshot;
  const miembros = snapshot.administracion.miembros.filter(m => {
    const baja = parseDdMmYyyy(m.fechaBaja);
    return !(baja > 0 && baja <= hasta);
  });
  return { ...snapshot, administracion: { ...snapshot.administracion, miembros } };
}
